import { AlertCircle, CloudOff, PlugZap, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError, errorMessage } from "@/api/client";
import { cn } from "@/lib/cn";

export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={cn("animate-spin text-current", className)} aria-hidden>
      <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2" />
      <path d="M21.5 12a9.5 9.5 0 0 0-9.5-9.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Centred thin arc — shown between routes (PlayAd behaviour). */
export function PageSpinner({ label }: { label?: string }) {
  return (
    <div className="flex min-h-[50vh] flex-1 flex-col items-center justify-center gap-3 text-muted" role="status" aria-live="polite">
      <Spinner size={28} className="text-fg/80" />
      {label && <span className="text-xs">{label}</span>}
    </div>
  );
}

/** Pulsing logo tile — Ad Monitor / dashboard loading splash. */
export function LogoLoader({ label }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4" role="status">
      <div className="grid size-20 animate-pulse-tile place-items-center rounded-[22px] bg-primary">
        <LogoMark className="size-9 text-white" />
      </div>
      {label && <span className="text-xs text-muted">{label}</span>}
    </div>
  );
}

export function LogoMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <path d="M8 23V9.5l8 7.2 8-7.2V23" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={cn("skeleton rounded-lg", className)} style={style} aria-hidden />;
}

export function SkeletonRows({ rows = 3, className, rowClassName }: { rows?: number; className?: string; rowClassName?: string }) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={cn("h-14", rowClassName)} />
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
  compact,
}: {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "gap-2 px-4 py-8" : "gap-3 px-6 py-14", className)}>
      {icon && <div className={cn("grid place-items-center rounded-2xl bg-elevated text-muted", compact ? "size-10" : "size-12")}>{icon}</div>}
      <div className={cn("font-semibold text-fg", compact ? "text-sm" : "text-base")}>{title}</div>
      {body && <div className="max-w-md text-[13px] text-muted text-balance">{body}</div>}
      {action && <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}

/** PlayAd "No data to show" panel, plus explicit network / missing-module variants. */
export function ErrorState({ error, onRetry, className, compact }: { error: unknown; onRetry?: () => void; className?: string; compact?: boolean }) {
  const e = error instanceof ApiError ? error : null;
  const network = e?.isNetwork;
  const missing = e?.isUnknownTool;
  const Icon = network ? CloudOff : missing ? PlugZap : AlertCircle;
  const title = network ? "Server unreachable" : missing ? "Waiting on the backend" : e?.status === 403 ? "No access" : "Something went wrong";
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 text-center", compact ? "px-4 py-6" : "px-6 py-12", className)} role="alert">
      <div className={cn("grid place-items-center rounded-full", missing ? "bg-amber-soft text-amber" : "bg-danger-soft text-danger", compact ? "size-8" : "size-10")}>
        <Icon className={compact ? "size-4" : "size-5"} strokeWidth={1.75} />
      </div>
      <div className="text-sm font-semibold">{title}</div>
      <div className="max-w-md text-[12.5px] text-muted text-balance">{errorMessage(error)}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-1 inline-flex items-center gap-1.5 text-xs text-link hover:text-lavender">
          <RefreshCw className="size-3.5" /> Retry
        </button>
      )}
    </div>
  );
}

/** "No data to show / Try a different date range or filter." */
export function NoData({ title = "No data to show", body = "Try a different date range or filter.", className }: { title?: string; body?: string; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-1.5 py-10 text-center", className)}>
      <div className="mb-1 grid size-8 place-items-center rounded-full border border-danger/60 text-danger">
        <span className="text-sm font-bold leading-none">!</span>
      </div>
      <div className="text-[13px] font-semibold">{title}</div>
      <div className="text-xs text-muted">{body}</div>
    </div>
  );
}

/**
 * Render helper for a React Query result: loading → skeleton/spinner, error → ErrorState,
 * empty (via `isEmpty`) → empty node, else children(data).
 */
export function QueryState<T>({
  query,
  loading,
  empty,
  isEmpty,
  children,
  compact,
}: {
  query: { data: T | undefined; isLoading: boolean; error: unknown; refetch: () => unknown; isError?: boolean };
  loading?: ReactNode;
  empty?: ReactNode;
  isEmpty?: (d: T) => boolean;
  children: (data: T) => ReactNode;
  compact?: boolean;
}) {
  if (query.isLoading) return <>{loading ?? <PageSpinner />}</>;
  if (query.error && query.data === undefined) return <ErrorState error={query.error} onRetry={() => void query.refetch()} compact={compact} />;
  if (query.data === undefined) return <>{loading ?? <PageSpinner />}</>;
  if (isEmpty?.(query.data)) return <>{empty ?? null}</>;
  return <>{children(query.data)}</>;
}
